import React, { useMemo } from "react";
import { Box, HStack, Text, VStack } from "@chakra-ui/react";

import { Button } from "@/components/ui/button";
import { REPORT_DETAIL_CARD_BORDER } from "./components/ReportsCardChrome";
import { REPORTS_DETAIL_MUTED_TEXT_CLASS } from "./reportsDetailTheme";
import { resolveEvidencePointerWithMeta } from "../../../utils/reportEvidencePointer";
import {
  evidencePointerTitle,
} from "../../../utils/reportEvidenceDisplay";
import { ReportEvidenceValueTable } from "./ReportEvidenceValueTable";
import { getSupportingSegmentDisplay } from "../../../utils/tesEvidencePointers";
import { resolveExplorerSliceIdFromEvidencePointer } from "../../../utils/managementExplorer";
import {
  mergeReportEvidenceSources,
  resolveMarketContextNavigationTarget,
  type MarketContextNavigationTarget,
} from "../../../utils/marketContextNavigation";

const REPORT_PRIMARY_FONT = "var(--font-primary)";
const REPORT_EVIDENCE_TITLE_COLOR = "#38BBD1";

export interface ReportEvidencePointersBlockProps {
  pointers: string[];
  title?: string;
  inputSnapshot: unknown;
  /** Full condition_analysis for pointer resolution when omitted from slim TES payload. */
  analysisEvidence?: unknown;
  primarySliceId?: string;
  subtleText: string;
  evidencePreBg?: string;
  evidencePreBorder?: string;
  /** Trade-management: jump to Market tab explorer for this slice. */
  onOpenExplorerSlice?: (sliceId: string) => void;
  /** Entry Analysis: jump to Market tab for cited evidence (per pointer). */
  onOpenMarketContext?: (target: MarketContextNavigationTarget) => void;
}

export function ReportEvidencePointersBlock({
  pointers,
  title = "Supporting evidence",
  inputSnapshot,
  analysisEvidence,
  primarySliceId,
  subtleText,
  onOpenExplorerSlice,
  onOpenMarketContext,
}: ReportEvidencePointersBlockProps): JSX.Element {
  const evidenceSnapshot = useMemo(
    () => mergeReportEvidenceSources(inputSnapshot, analysisEvidence),
    [analysisEvidence, inputSnapshot]
  );

  const supportingSegment = useMemo(
    () => getSupportingSegmentDisplay(evidenceSnapshot, primarySliceId),
    [evidenceSnapshot, primarySliceId]
  );

  const segmentExplorerId = useMemo(() => {
    const fromPrimary = (primarySliceId ?? "").trim();
    if (fromPrimary) return fromPrimary;
    return supportingSegment?.sliceId?.trim() || null;
  }, [primarySliceId, supportingSegment?.sliceId]);

  const items = useMemo(
    () =>
      pointers
        .slice(0, 8)
        .map((pointer) => {
          const resolved = resolveEvidencePointerWithMeta(evidenceSnapshot, pointer, {
            primarySliceId,
          });
          const marketNav = onOpenMarketContext
            ? resolveMarketContextNavigationTarget(evidenceSnapshot, pointer)
            : null;
          const explorerSliceId = onOpenExplorerSlice
            ? resolveExplorerSliceIdFromEvidencePointer(evidenceSnapshot, pointer)
            : null;
          return {
            pointer,
            value: resolved.value,
            warning: resolved.warning,
            title: evidencePointerTitle(pointer),
            marketNav,
            explorerSliceId,
          };
        })
        .filter((item) => item.value !== undefined),
    [evidenceSnapshot, onOpenExplorerSlice, onOpenMarketContext, pointers, primarySliceId]
  );

  const showPerItemExplorerNav = Boolean(onOpenExplorerSlice && !segmentExplorerId);

  if (!supportingSegment && items.length === 0) {
    return <></>;
  }

  return (
    <Box mb="10px">
      <Text
        data-report-evidence-title=""
        fontSize="xs"
        color={REPORT_EVIDENCE_TITLE_COLOR}
        mb="8px"
        fontFamily={REPORT_PRIMARY_FONT}
        fontWeight="medium"
      >
        {title}
      </Text>

      {supportingSegment ? (
        <Box
          data-report-evidence-segment=""
          borderWidth="1px"
          borderStyle="solid"
          borderColor={REPORT_DETAIL_CARD_BORDER}
          borderRadius="6px"
          p="10px"
          mb="12px"
          w="100%"
        >
          <Text
            data-report-evidence-title=""
            fontSize="xs"
            color={REPORT_EVIDENCE_TITLE_COLOR}
            mb="4px"
            fontFamily={REPORT_PRIMARY_FONT}
            fontWeight="medium"
          >
            Supporting segment
          </Text>
          <ReportEvidenceValueTable
            pointer="supporting_segment"
            value={{
              segment: supportingSegment.segmentLabel,
              ...(supportingSegment.sampleSize != null
                ? { sample_size: supportingSegment.sampleSize }
                : {}),
              ...(supportingSegment.fromCatalog
                ? {}
                : { segment_id: supportingSegment.sliceId }),
            }}
          />
          {onOpenExplorerSlice && segmentExplorerId ? (
            <HStack spacing={2} mt="8px" flexWrap="wrap">
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                data-font="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenExplorerSlice(segmentExplorerId);
                }}
              >
                View in Exit Placement Explorer
              </Button>
            </HStack>
          ) : null}
        </Box>
      ) : null}

      <VStack align="stretch" spacing="12px">
        {items.map((item) => (
          <Box
            key={item.pointer}
            data-report-evidence-item=""
            borderWidth="1px"
            borderStyle="solid"
            borderColor={REPORT_DETAIL_CARD_BORDER}
            borderRadius="6px"
            p="10px"
            w="100%"
          >
            <HStack
              justify="space-between"
              align="flex-start"
              spacing={3}
              mb={item.marketNav || (showPerItemExplorerNav && item.explorerSliceId) ? 4 : 0}
            >
              <Text
                data-report-evidence-title=""
                fontSize="xs"
                color={REPORT_EVIDENCE_TITLE_COLOR}
                fontFamily={REPORT_PRIMARY_FONT}
                fontWeight="medium"
                textTransform="none"
                flex="1"
              >
                {item.title}
              </Text>
            </HStack>
            {item.warning ? (
              <Text
                fontSize="xs"
                color="orange.500"
                mb="6px"
                lineHeight="1.4"
                fontFamily={REPORT_PRIMARY_FONT}
              >
                {item.warning}
              </Text>
            ) : null}
            <ReportEvidenceValueTable pointer={item.pointer} value={item.value} />
            {item.marketNav && onOpenMarketContext ? (
              <Box mt="10px">
                {item.marketNav.label ? (
                  <Text
                    fontSize="sm"
                    data-report-evidence-body=""
                    className={REPORTS_DETAIL_MUTED_TEXT_CLASS}
                    mb="8px"
                    fontFamily={REPORT_PRIMARY_FONT}
                    lineHeight="1.45"
                  >
                    {item.marketNav.label}
                  </Text>
                ) : null}
                <Button
                  type="button"
                  variant="outline-brand"
                  size="sm"
                  data-font="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenMarketContext(item.marketNav!);
                  }}
                >
                  View in Market tab
                </Button>
              </Box>
            ) : null}
            {showPerItemExplorerNav && item.explorerSliceId ? (
              <Box mt="10px">
                <Button
                  type="button"
                  variant="outline-brand"
                  size="sm"
                  data-font="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenExplorerSlice!(item.explorerSliceId!);
                  }}
                >
                  View in Exit Placement Explorer
                </Button>
              </Box>
            ) : null}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}
